import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from '../shared/Toast';

const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };
const hintStyle = { color: colors.textFaint, fontSize: '0.75rem', marginTop: '0.3rem' };
const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };

export default function BookSettingsSection({ user }) {
  const { toast, showToast } = useToast();
  const [cashoutConfig, setCashoutConfig] = useState({
    cashout_enabled: false, house_margin_percentage: 5.0, min_cashout_amount: 1.0,
    allow_free_play_cashout: false
  });
  const [savingCashout, setSavingCashout] = useState(false);
  const [csForwarding, setCsForwarding] = useState(user.cs_forwarding_enabled || false);
  const [agentCsForwarding, setAgentCsForwarding] = useState(user.agent_cs_forwarding_enabled || false);
  const [savingCs, setSavingCs] = useState(false);
  const [openSections, setOpenSections] = useState(new Set(['cashout']));

  const toggleSection = (key) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  useEffect(() => {
    (async () => {
      const [config, freshUser] = await Promise.all([
        api.getCashoutConfig(),
        api.getAccount(user.id)
      ]);
      setCashoutConfig(config);
      setCsForwarding(freshUser.cs_forwarding_enabled || false);
      setAgentCsForwarding(freshUser.agent_cs_forwarding_enabled || false);
    })();
  }, [user.id]);

  const saveCashoutConfig = async () => {
    setSavingCashout(true);
    try {
      const result = await api.updateCashoutConfig(cashoutConfig);
      setCashoutConfig(prev => ({ ...prev, ...result }));
      showToast('Cashout settings saved');
    } catch (err) {
      showToast('Failed to save cashout settings: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingCashout(false);
    }
  };

  const saveCsForwarding = async (customerVal, agentVal) => {
    // Optimistically reflect the toggle immediately — a controlled checkbox
    // whose state only updates after the await visually snaps back to the old
    // value while the request is in flight, which reads as "didn't save".
    const prevCustomer = csForwarding;
    const prevAgent = agentCsForwarding;
    setCsForwarding(customerVal);
    setAgentCsForwarding(agentVal);
    setSavingCs(true);
    try {
      await api.updateCsForwarding(customerVal, agentVal);
      showToast('Forwarding setting saved');
    } catch (err) {
      setCsForwarding(prevCustomer);
      setAgentCsForwarding(prevAgent);
      showToast('Failed to save forwarding setting', 'error');
    } finally {
      setSavingCs(false);
    }
  };

  const sectionCard = { background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, marginBottom: '1rem' };
  const sectionHeader = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.textPrimary, fontFamily, fontWeight: 600, fontSize: '1rem' };
  const saveButtonStyle = { background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <ToastHost toast={toast} />
      <h2 style={{ color: colors.textPrimary, marginBottom: '1rem' }}>Book Settings</h2>

      {/* Cashout Configuration */}
      <div style={sectionCard}>
        <button onClick={() => toggleSection('cashout')} style={sectionHeader}>
          <span>Cashout Feature</span>
          {openSections.has('cashout') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
        </button>
        {openSections.has('cashout') && (
          <div style={{ padding: '0 1.25rem 1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 6, background: colors.sectionBg, marginBottom: '1rem' }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Enable Cashout</p>
                <p style={{ fontSize: '0.8rem', color: colors.textMuted }}>Allow customers to cash out parlays early</p>
              </div>
              <input
                type="checkbox"
                checked={cashoutConfig.cashout_enabled}
                onChange={e => setCashoutConfig(prev => ({ ...prev, cashout_enabled: e.target.checked }))}
                style={{ width: 20, height: 20, accentColor: colors.gold, cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>House Margin (%)</label>
                <input type="number" step="0.1" min="0" max="50" value={cashoutConfig.house_margin_percentage} onChange={e => setCashoutConfig(prev => ({ ...prev, house_margin_percentage: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                <p style={hintStyle}>Your cut from each cashout.</p>
              </div>
              <div>
                <label style={labelStyle}>Minimum Cashout ($)</label>
                <input type="number" step="0.01" min="0" value={cashoutConfig.min_cashout_amount} onChange={e => setCashoutConfig(prev => ({ ...prev, min_cashout_amount: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                <p style={hintStyle}>Minimum cashout value allowed.</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="allow_free_play_cashout"
                checked={cashoutConfig.allow_free_play_cashout}
                onChange={e => setCashoutConfig(prev => ({ ...prev, allow_free_play_cashout: e.target.checked }))}
                style={{ width: 18, height: 18, accentColor: colors.gold, cursor: 'pointer' }}
              />
              <label htmlFor="allow_free_play_cashout" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>Allow Cashout on Free Play Bets</label>
            </div>
            <p style={{ ...hintStyle, marginTop: '-0.75rem', marginBottom: '1rem', marginLeft: 26 }}>When disabled, free play parlays cannot be cashed out.</p>
            <button onClick={saveCashoutConfig} disabled={savingCashout} style={saveButtonStyle}>
              {savingCashout ? 'Saving...' : 'Save Cashout Settings'}
            </button>
          </div>
        )}
      </div>

      {/* Customer Support Forwarding */}
      <div style={sectionCard}>
        <button onClick={() => toggleSection('cs')} style={sectionHeader}>
          <span>Customer Support Forwarding</span>
          {openSections.has('cs') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
        </button>
        {openSections.has('cs') && (
          <div style={{ padding: '0 1.25rem 1.25rem' }}>
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '1rem' }}>
              When enabled, messages are automatically forwarded to Customer Service in addition to appearing in your inbox.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 6, background: colors.sectionBg, marginBottom: '0.5rem', opacity: savingCs ? 0.5 : 1 }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Forward Customer Messages</p>
                <p style={{ fontSize: '0.8rem', color: colors.textMuted }}>Customer messages to you are copied to CS</p>
              </div>
              <input
                type="checkbox"
                checked={csForwarding}
                disabled={savingCs}
                onChange={e => saveCsForwarding(e.target.checked, agentCsForwarding)}
                style={{ width: 20, height: 20, accentColor: colors.gold, cursor: savingCs ? 'default' : 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 6, background: colors.sectionBg, opacity: savingCs ? 0.5 : 1 }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Forward Agent Messages</p>
                <p style={{ fontSize: '0.8rem', color: colors.textMuted }}>Agent messages to you are copied to CS</p>
              </div>
              <input
                type="checkbox"
                checked={agentCsForwarding}
                disabled={savingCs}
                onChange={e => saveCsForwarding(csForwarding, e.target.checked)}
                style={{ width: 20, height: 20, accentColor: colors.gold, cursor: savingCs ? 'default' : 'pointer' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
